import { useState, useEffect, useRef } from "react";
import {
  FiGlobe, FiLink, FiGitBranch, FiTrash2,
  FiSearch, FiAlertCircle, FiArrowRight,
} from "react-icons/fi";
import { api } from "../../services/api";

const DATA_TYPES = [
  { id: "website", icon: FiGlobe,    label: "Website",     desc: "Crawl all pages from your website automatically" },
  { id: "single",  icon: FiLink,     label: "Single Link", desc: "Train on one specific URL or landing page" },
  { id: "sitemap", icon: FiGitBranch,label: "Sitemap",     desc: "Import all pages via sitemap.xml" },
];

export default function StepOne({ updateStep, updateAllUrls }) {
  const [type,         setType]         = useState("website");
  const [url,          setUrl]          = useState("");
  const [urls,         setUrls]         = useState([]);
  const [selected,     setSelected]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [crawledCount, setCrawledCount] = useState(0);
  const [currentUrl,   setCurrentUrl]   = useState("");
  const [dots,         setDots]         = useState("");
  const abortRef = useRef(null);

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setDots((d) => (d.length >= 3 ? "" : d + ".")), 500);
    return () => clearInterval(t);
  }, [loading]);

  // Cleanup on unmount
  useEffect(() => () => abortRef.current?.abort(), []);

  const normalizeInputUrl = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return "";
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  const isValidUrl = (raw) => {
    try {
      const u = new URL(normalizeInputUrl(raw));
      // must have a real hostname with at least one dot (e.g. trackpilots.com)
      return u.hostname.includes(".");
    } catch {
      return false;
    }
  };

  const handleFetchUrls = async () => {
    if (!url.trim()) { setError("Please enter a URL"); return; }
    if (!isValidUrl(url)) { setError("Please enter a valid URL (e.g. https://your-website.com)"); return; }

    const normalizedUrl = normalizeInputUrl(url);

    // For single link or sitemap — no crawl needed, just use the URL directly
    if (type !== "website") {
      setUrls([normalizedUrl]);
      setSelected([normalizedUrl]);
      setUrl(normalizedUrl);
      setError("");
      return;
    }

    // Abort any previous request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError("");
    setUrls([]);
    setSelected([]);
    setCrawledCount(0);
    setCurrentUrl("");

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${api.baseUrl}/crawler/fetch-urls-stream?url=${encodeURIComponent(normalizedUrl)}`,
        { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
      );

      if (!res.ok) throw new Error("Crawler request failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      const discovered = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        for (const line of text.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.status === "crawled" && event.url) {
              discovered.push(event.url);
              setUrls([...discovered]);
              setSelected([...discovered]);
              setCrawledCount(discovered.length);
              setCurrentUrl(event.url);
            }

            if (event.status === "complete") {
              const final = event.urls?.length ? event.urls : discovered;
              if (final.length === 0) {
                setError("This website blocks automated crawling. Try the Sitemap option and enter your sitemap.xml URL directly.");
              }
              setUrls(final);
              setSelected(final);
              setCrawledCount(final.length);
            }

            if (event.status === "error") {
              setError(event.message || "Crawl failed");
            }
          } catch {}
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") setError(err.message || "Failed to fetch URLs");
    } finally {
      setLoading(false);
      setCurrentUrl("");
    }
  };

  const toggleSelectAll = () =>
    setSelected(selected.length === urls.length ? [] : [...urls]);

  const toggleSelect = (u) =>
    setSelected((prev) => prev.includes(u) ? prev.filter((x) => x !== u) : [...prev, u]);

  const deleteUrl = (u) => {
    setUrls((prev) => prev.filter((x) => x !== u));
    setSelected((prev) => prev.filter((x) => x !== u));
  };

  const handleContinue = () => {
    updateAllUrls(selected);
    updateStep(2);
  };

  const urlLabel       = type === "single" ? "Page URL" : type === "sitemap" ? "Sitemap URL" : "Website URL";
  const urlPlaceholder = type === "sitemap" ? "https://your-website.com/sitemap.xml" : "https://your-website.com";
  const btnLabel       = type === "website" ? "Fetch URLs" : "Use This URL";

  return (
    <div className="max-w-2xl mx-auto space-y-5">

      {/* Source type cards */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-3">Choose your data source</p>
        <div className="grid grid-cols-3 gap-3">
          {DATA_TYPES.map(({ id, icon: Icon, label, desc }) => (
            <button
              key={id}
              onClick={() => { setType(id); setUrls([]); setSelected([]); setError(""); }}
              className={`flex flex-col items-center gap-2.5 p-4 rounded-2xl border-2 text-center transition-all ${
                type === id
                  ? "border-primary bg-primary-light shadow-sm"
                  : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${
                type === id ? "bg-primary text-white shadow-sm shadow-primary/30" : "bg-gray-100 text-gray-500"
              }`}>
                <Icon size={19} />
              </div>
              <p className={`text-sm font-semibold leading-tight ${type === id ? "text-primary" : "text-gray-800"}`}>{label}</p>
              <p className="text-[11px] text-gray-400 leading-snug">{desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* URL input */}
      <div className="bg-white border rounded-2xl p-5 space-y-3">
        <label className="block text-sm font-semibold text-gray-700">{urlLabel}</label>
        <div className="flex gap-2.5">
          <div className="flex-1 flex items-center gap-2.5 border rounded-xl px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary bg-white transition">
            <FiGlobe size={15} className="text-gray-400 shrink-0" />
            <input
              type="url"
              value={url}
              onChange={(e) => { setUrl(e.target.value); setError(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleFetchUrls()}
              placeholder={urlPlaceholder}
              className="flex-1 text-sm outline-none text-gray-800 placeholder-gray-400 bg-transparent"
            />
          </div>
          <button
            onClick={handleFetchUrls}
            disabled={loading}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition shrink-0 ${
              loading
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-primary text-white hover:opacity-90 shadow-sm shadow-primary/25"
            }`}
          >
            {loading
              ? <span className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
              : <FiSearch size={14} />}
            {loading ? "Crawling…" : btnLabel}
          </button>
        </div>
        {error && (
          <p className="flex items-center gap-1.5 text-xs text-red-500 font-medium">
            <FiAlertCircle size={12} /> {error}
          </p>
        )}
      </div>

      {/* Crawling progress */}
      {loading && (
        <div className="bg-white border rounded-2xl p-8 flex flex-col items-center gap-5">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-4 border-primary-light border-t-primary animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <FiGlobe size={26} className="text-primary" />
            </div>
          </div>
          <div className="text-center space-y-1">
            <p className="font-semibold text-gray-900 text-base">Crawling your website{dots}</p>
            <p className="text-sm text-gray-500">
              <span className="text-primary font-semibold">{crawledCount}</span> URL{crawledCount !== 1 ? "s" : ""} fetching{dots}
            </p>
          </div>
          {currentUrl && (
            <div className="w-full bg-gray-50 rounded-xl px-4 py-2.5 text-center border">
              <p className="text-xs text-primary font-mono truncate">{currentUrl}</p>
            </div>
          )}
          <div className="w-full space-y-1.5">
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-indigo-400 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(5 + (crawledCount / 200) * 95, 95)}%` }}
              />
            </div>
            <p className="text-[11px] text-gray-400 text-center">Keep this tab open — large sites may take a few minutes</p>
          </div>
        </div>
      )}

      {/* URL results list */}
      {!loading && urls.length > 0 && (
        <div className="bg-white border rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.length === urls.length && urls.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded accent-primary cursor-pointer"
              />
              <span className="text-sm font-semibold text-gray-700">
                {selected.length === urls.length ? "Deselect all" : "Select all"}
              </span>
            </label>
            <span className="text-xs font-semibold bg-primary-light text-primary px-2.5 py-1 rounded-full">
              {urls.length} URL{urls.length !== 1 ? "s" : ""} found
            </span>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y">
            {urls.map((u) => {
              const isChecked = selected.includes(u);
              return (
                <div
                  key={u}
                  onClick={() => toggleSelect(u)}
                  className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors ${isChecked ? "bg-primary-light/50" : "hover:bg-gray-50"}`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleSelect(u)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 rounded accent-primary cursor-pointer shrink-0"
                  />
                  <FiLink size={11} className="text-gray-400 shrink-0" />
                  <span className="flex-1 text-sm text-gray-700 truncate font-mono text-[12px]">{u}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteUrl(u); }}
                    className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition shrink-0"
                  >
                    <FiTrash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-1 pb-4">
        <p className="text-sm text-gray-500">
          {loading
            ? <><span className="font-semibold text-primary">{crawledCount}</span><span className="text-gray-400"> URL{crawledCount !== 1 ? "s" : ""} fetching{dots}</span></>
            : urls.length > 0 && selected.length < urls.length &&
              <><span className="font-semibold text-primary">{selected.length}</span><span className="text-gray-400"> / {urls.length} URLs selected</span></>
          }
        </p>
        <div className="flex items-center gap-3">
          {/* Skip option — visible when no URLs selected and not crawling */}
          {!loading && selected.length === 0 && (
            <button
              onClick={() => { updateAllUrls([]); updateStep(2); }}
              className="text-sm text-gray-400 hover:text-gray-600 underline underline-offset-2 transition"
            >
              Skip for now
            </button>
          )}
          <button
            disabled={loading || selected.length === 0}
            onClick={handleContinue}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition ${
              loading || selected.length === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-primary text-white hover:opacity-90 shadow-sm shadow-primary/25"
            }`}
          >
            Continue <FiArrowRight size={14} />
          </button>
        </div>
      </div>

    </div>
  );
}
